<template>
    <v-dialog v-model="inputValue" app width="auto" style="overflow: hidden">
        <app-card class="app-media-library" size="x-large" style="max-height: 85vh">
            <template #header>
                <h2 v-if="heading" class="text-h4 mb-2">
                    {{ heading }}
                </h2>
                <field-string
                    v-model="search"
                    :label="$t('search')"
                    :loading="searching"
                    hide-details
                    class="mb-2"
                    :autocomplete="false"
                >
                    <template v-if="search" #append>
                        <app-button icon="clear" icon-size="large" @click="search = null" />
                    </template>
                </field-string>
            </template>
            <div class="app-media-library__body">
                <div v-for="item in mediaItems" :key="item.id" class="app-media-library__preview">
                    <app-media-preview
                        selectable
                        :media="item"
                        :selected="selected.includes(item.id)"
                        @selected="onSelectMedia"
                    />
                </div>
                <template v-if="!searching && items.length === 0">
                    <app-lacuna
                        icon="media"
                        :title="$t('we_couldnt_find_any_media_in_this_library') + '.'"
                        :text="search ? $t('try_searching_for_something_else') : null"
                    />
                </template>
            </div>
            <template #footer>
                <app-button
                    :disabled="searching"
                    :text="$t('add_item')"
                    icon="add"
                    secondary
                    @click="createMediaModal = true"
                />
                <v-spacer />
                <app-button-cancel class="ml-2" @click="onCancel" />
                <app-button
                    :disabled="searching"
                    :title="$t('save')"
                    :text="submitButtonText"
                    :icon="getIcon('save', 'fas')"
                    class="ml-2"
                    @click="onSave"
                />
            </template>
        </app-card>

        <library-media-modal
            v-if="model && isGrowthInvest()"
            v-model="createMediaModal"
            media-id="create"
            :model="model"
            :preset-attr="collection.preset_attr"
            :readonly-attr="Object.keys(collection.preset_attr)"
            close-after-save
            @input="createMediaModal = $event"
            @save:media="onMediaCreated"
        />
    </v-dialog>
</template>

<script>
import { Media } from '@/models';
import strim from '@/lib/helpers/strim';

export default {
    name: 'AppMediaLibrary',
    components: {
        LibraryMediaModal: () =>
            import(
                /* webpackChunkName: "library" */
                '@/views/LibraryMediaModal.vue'
            )
    },
    data: () => ({
        search: null,
        searching: false,
        preset: [],
        items: [],
        selected: [],
        Media,
        createMediaModal: false
    }),
    computed: {
        inputValue: {
            get() {
                return this.$store.getters['app/show_media_library'];
            },
            set(visible) {
                this.$store.dispatch('app/setMediaLibraryVisibility', visible);
            }
        },
        submitButtonText() {
            return this.$t('save');
        },
        heading() {
            return this.$store.getters['app/media_library_heading'];
        },
        collection() {
            return this.$store.getters['app/media_library_collection'];
        },
        model() {
            return this.$store.getters['app/media_library_model'];
        },
        selectedCallback() {
            return this.$store.getters['app/media_library_selected_callback'];
        },
        multiple() {
            return this.$store.getters['app/media_library_multiple'];
        },
        preselected() {
            return this.$store.getters['app/media_library_preselected'];
        },
        filters() {
            let filters = {};

            if (this.search && strim(this.search).length > 0) {
                filters.name = `${this.getFilterOperator('contains')}:${this.search}`;
            }

            return filters;
        },
        mediaItems() {
            return this.items.map(item => new Media(item));
        },
        selectedMedia() {
            const found = this.mediaItems.filter(item => this.selected.includes(item.id));

            if (found.length === 0) {
                return null;
            }

            if (this.multiple) {
                return found;
            }

            return found[0];
        }
    },
    watch: {
        collection() {
            this.search = null;
            this.querySearch(null);
        },
        preselected() {
            this.setPreselected();
        },
        search(v) {
            this.querySearchDebounced(v);
        },
        inputValue(v) {
            if (!v) {
                this.items = [];
                this.search = null;
                this.querySearch(null);
                this.setPreselected();
            }
        }
    },
    async mounted() {
        this.querySearchDebounced = this._.debounce(this.querySearch, 500);
    },
    methods: {
        resetItems() {
            this.items = this.defaultItems;
        },
        async querySearch(v) {
            if (!this.collection) {
                return;
            }

            this.searching = true;

            try {
                const results = await this.collection.request(null, null, [], this.filters, {
                    persistent: v === null
                });

                if (results && Array.isArray(results.data)) {
                    this.items = results.data;
                    this.preset = v ? this.items : this.preset;
                } else {
                    this.items = [];
                }
            } catch (error) {
                this.handleError(error, () => this.querySearch(v));
            }

            this.searching = false;
        },
        onSelectMedia(itemId) {
            if (this.selected.includes(itemId)) {
                this.selected = this.selected.filter(id => id !== itemId);
            } else {
                if (this.multiple) {
                    this.selected.push(itemId);
                } else {
                    this.selected = [itemId];
                }
            }
        },
        onCancel() {
            this.inputValue = false;
        },
        onSave() {
            return this.selectedCallback(this.selectedMedia);
        },
        setPreselected() {
            if (this.preselected && Array.isArray(this.preselected)) {
                this.selected = this.preselected;
            } else if (this.preselected) {
                this.selected = [this.preselected];
            } else {
                this.selected = [];
            }
        },
        onMediaCreated(media) {
            this.search = null;
            this.selected = [media.id];
            this.querySearch(null);
        }
    }
};
</script>
<style lang="scss">
.app-media-library {
    display: block;
    &__body {
        display: flex;
        flex-wrap: wrap;
        align-items: flex-start;
        justify-content: start;
        align-content: flex-start;
        min-height: 16.4rem;
    }
    &__preview {
        margin: 0.4rem;
        flex: 7.7rem 0;
    }
}
</style>
